import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as api from './api';
import type { User } from './types';

interface AuthState {
  user: User | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  setUser: (u: User) => void;
  /** Adopt a token+user returned by an endpoint other than login (e.g. password reset). */
  adoptSession: (token: string, user: User) => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(Boolean(api.getToken()));

  // Validate a stored token against the server on boot.
  useEffect(() => {
    if (!api.getToken()) return;
    api.me()
      .then((r) => setUser(r.user))
      .catch((e: unknown) => { if (e instanceof api.ApiError && e.status === 401) api.setToken(null); })
      .finally(() => setLoading(false));
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    const r = await api.login(username, password);
    api.setToken(r.token);
    setUser(r.user);
    qc.clear();
  }, [qc]);

  const adoptSession = useCallback((token: string, u: User) => { api.setToken(token); setUser(u); qc.clear(); }, [qc]);

  const signOut = useCallback(() => {
    api.setToken(null);
    setUser(null);
    qc.clear();
  }, [qc]);

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, setUser, adoptSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
